import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export default function AdminLoginModal({ onSuccess, onCancel }) {
  const t = useT();
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const initialSentRef = useRef(false);

  const sendCode = async () => {
    setSending(true);
    setError('');
    setInfo('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        setInfo(t('admin.codeSent'));
        setResendIn(60);
      } else {
        setError(data.message || t('admin.sendFailed'));
      }
    } catch {
      setError(t('error.connection'));
    } finally {
      setSending(false);
    }
  };

  // Auto-send code the first time the modal opens.
  useEffect(() => {
    if (initialSentRef.current) return;
    initialSentRef.current = true;
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError(t('admin.codeLength'));
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.success && data.data?.accessToken) {
        localStorage.setItem('adminToken', data.data.accessToken);
        onSuccess(data.data.accessToken);
        return;
      }
      setError(data.message || t('admin.verifyFailed'));
    } catch {
      setError(t('error.connection'));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="auth-overlay" onClick={onCancel}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-header">
          <h3>{t('admin.loginTitle')}</h3>
          <button className="auth-close" onClick={onCancel}>&times;</button>
        </div>

        <form onSubmit={handleVerify} className="auth-form">
          <p className="admin-login-hint">{t('admin.loginHint')}</p>

          <div className="auth-field">
            <label>{t('admin.code')}</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
            />
          </div>

          {info && <div className="auth-info">{info}</div>}
          {error && <div className="auth-error">{error}</div>}

          <button className="auth-submit" type="submit" disabled={verifying || code.length !== 6}>
            {verifying ? t('auth.wait') : t('admin.verify')}
          </button>

          <button
            type="button"
            className="admin-resend-btn"
            onClick={sendCode}
            disabled={sending || resendIn > 0}
          >
            {sending
              ? t('profilePage.sending')
              : resendIn > 0
                ? `${t('profilePage.resendIn')} ${resendIn}s`
                : t('profilePage.resend')}
          </button>
        </form>
      </div>
    </div>
  );
}
